import md5 from 'md5';
import moment from 'moment';
import { FieldState, FormState, ValidatorResponse } from 'formstate';
import { showToastNotification, Validators } from 'welltrax-ui-components';
import { ILayer } from 'welltrax-ui-components/lib/types/Form/Inputs/ILayer';
import { capitalize, cloneDeep, find, get, groupBy, map, orderBy, replace, upperCase } from 'lodash';
import { InjectedIntl } from 'react-intl';

import { setPhoneNumberMask } from '../../modules/commons/components/PhoneBook/utils';
import { SetupStore } from '../../modules/commons/store/SetupStore';
import { Driver, Load } from '../../modules/dispatch/components/dispatchLoad/types/DispatchBoardTypes';
import { IColumnsExport } from '../../modules/finance/commons/types/IInvoicePayrollExport';
import { ProfileUtils } from '../components/commons/Profiles/utils/ProfileUtils';
import { FieldProps, FieldPropsNoInline } from '../interface/FieldProps';
import { DateTimeType } from '../types/DateTimeType';
import {
  EShortcuts,
  ExtraFormat,
  IColumnsRep,
  IConditionalsType,
  IFilterType,
  IShortcuts,
  IVeltraxProfileType
} from '../types/ProfileTypes';
import { intl } from './Intl';
import { checkIfNumber } from './utilsForm';

export const flattenMessages = (nestedMessages: any, prefix = '') => {
  return Object.keys(nestedMessages).reduce((messages: any, key: any) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      messages[prefixedKey] = value;
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }
    return messages;
  }, {});
};

export const validateAndGetObject = (entity: object, keyMap: string): any => {
  let value = Object.assign({}, entity) as any;
  keyMap.split('.').forEach(key => {
    value = value && value[key];
  });
  return value;
};

export const validateAndGetArray = (entity: object, keyMap: string): any => {
  let value = Object.assign({}, entity) as any;
  keyMap.split('.').forEach(key => {
    value = value && value[key];
  });

  if (value) {
    if (!!value.length) return value;
  }
  return value;
};

export const replaceAll = (value: string, replaceValue: string, replaceWith: string) => {
  let newValue = value;
  while (newValue.includes(replaceValue)) {
    newValue = newValue.replace(replaceValue, replaceWith);
  }
  return newValue;
};

export const getDate = (date: string | null): Date | null => {
  if (date) {
    return moment(date).toDate();
  }
  return null;
};

export const getDatesObject = (date: Date, time?: Date, minimal?: boolean): any => {
  const normalTime = moment(time)?.isValid() && moment.isDate(time) ? moment(time).format('hh:mm A') : '12:00 am';
  const armyTime = moment(time)?.isValid() && moment.isDate(time) ? moment(time).format('HH:mm') : '00:00';
  if (!date || minimal) {
    return {
      time: armyTime,
      date: (date && moment(date).format('MM/DD/YYYY')) || ''
    };
  }
  return {
    date: moment(date).format('MM/DD/YYYY'),
    empty: false,
    javaDate: `${moment(date).format('MMM DD, YYYY')} ${normalTime}`,
    sqldate: moment(date).format('YYYY-MM-DD'),
    sqldateWithQuotes: `'${moment(date).format('YYYY-MM-DD')}'`,
    sqltimestamp: `${moment(date).format('MMM DD, YYYY')} ${normalTime}`,
    sqltimestampWithMilliseconds: `${moment(date).format('MMM DD, YYYY')} ${normalTime}`,
    sqltimestampWithQuotes: `'${moment(date).format('YYYY-MM-DD HH:mm:ss.SSS')}'`,
    time: armyTime,
    timeInMilliseconds: date.getTime(),
    timeInMinutes: date.getTime() / 60000
  };
};

export const getStringFromDate = (date: Date) => {
  return moment(date).format('HH:mm');
};

export const parseCoverStylesForSave = (data: any) => {
  const totalsObj = data.$.totals.$;
  const ticketsObj = data.$.tickets.$;
  const orientation = data.$.orientation.value;
  const paper = data.$.paper.value;
  const totals: any = {};
  const tickets: any = {};
  Object.entries(totalsObj).forEach(([key, value]) => {
    const newValue: any = value;
    totals[key] = newValue?.value;
  });

  Object.entries(ticketsObj).forEach(([key, value]) => {
    const newValue: any = value;
    tickets[key] = newValue?.value;
  });

  return { totals, tickets, orientation, paper };
};

export const getDisplayFormat = (value: any, extraFormat?: ExtraFormat | null) => {
  let newValue = getNumberWithDecimalPlaces(value, extraFormat);
  if (extraFormat?.isThousandSeparator) newValue = numberWithCommas(newValue);
  return extraFormat?.format
    ? extraFormat?.format === '$'
      ? `$${newValue}`
      : extraFormat?.format === '%'
      ? `${newValue}%`
      : newValue
    : newValue;
};

const getNumberWithDecimalPlaces = (value: any, extraFormat?: ExtraFormat | null) => {
  return Number(value).toFixed(
    checkIfNumber(extraFormat?.numberOfDecimals) && extraFormat?.numberOfDecimals !== null
      ? extraFormat?.numberOfDecimals
      : undefined
  );
};

export const getFormatValue = (
  field: any,
  value: any,
  columns: (IColumnsRep | IColumnsExport)[],
  onlyDecimalFormat?: boolean
) => {
  let newValue = value;
  const foundDynamicField = columns.find(columns => columns.dynamicFieldId === field);
  if (foundDynamicField?.extraFormat) {
    newValue = getNumberWithDecimalPlaces(newValue, foundDynamicField?.extraFormat);
    return onlyDecimalFormat ? newValue : getDisplayFormat(newValue, foundDynamicField?.extraFormat);
  }
  return Number(newValue).toFixed(2) || 0;
};

const numberWithCommas = (value: any) => {
  return value.toString().replace(/\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g, ',');
};

export const getDateFromStringHour = (hour: String) => {
  return new Date(`October 16, 1991 ${hour}:00`);
};

const arrayMoveMutate = (array: any[], from: number, to: number) => {
  array.splice(to < 0 ? array.length + to : to, 0, array.splice(from, 1)[0]);
};

export const arrayMove = (array: any[], from: number, to: number) => {
  const arrayAux = array.slice();
  arrayMoveMutate(arrayAux, from, to);
  return arrayAux;
};

export const move = (arr: any[], old_index: number, new_index: number) => {
  const l = Array.from(arr);
  const from = l[old_index];
  l[old_index] = l[new_index];
  l[new_index] = from;
  return l;
};

export const orderData = (data: IColumnsRep[], order: string[]) => {
  return orderBy(data, order);
};

export const reorderData = (data: IColumnsRep[]) => {
  return data.map((data: IColumnsRep, index: number) => {
    return {
      ...data,
      order: index + 1
    };
  });
};

export const getFieldProps = (
  form: FormState<any>,
  key: string,
  disabled: boolean,
  intlId: string,
  defaultLabel: string,
  noFill?: boolean,
  noInline?: boolean
): FieldProps => {
  const options: FieldProps = {
    disabled,
    inline: !noInline,
    fill: true,
    id: key,
    layer: {
      labelWidth: 4,
      inputWidth: 8,
      labelOrientation: 'end'
    },
    fieldState: form.$[key],
    label: `${intl(intlId, defaultLabel || '')}:`
  };

  if (noFill) {
    delete options.fill;
  }
  return options;
};

export const getFieldPropsNoInline = (
  form: FormState<any>,
  key: string,
  disabled: boolean,
  intlId: string,
  defaultLabel: string,
  noFill?: boolean,
  layerField?: ILayer
): FieldPropsNoInline => {
  const layer = layerField ? layerField : {};
  const options: FieldPropsNoInline = {
    ...{ layer },
    disabled,
    fill: true,
    id: key,
    fieldState: form.$[key],
    label: `${intl(intlId, defaultLabel || '')}:`
  };

  if (noFill) {
    delete options.fill;
  }
  return options;
};

export const onlyNumericCharacters =
  (intl: InjectedIntl) =>
  (value: any): ValidatorResponse => {
    const regex = /^[0-9]*$/;
    return (!!value && !`${value}`.match(regex) && intl.formatMessage({ id: 'app.errors.onlyPositiveNumbers' })) || '';
  };

export const cleanString = (word: string) => {
  const replaceSpace = replace(word, /_/g, ' ');
  return replaceSpace.replace('$$4', '/4 ');
};

export const requiredIfFieldState =
  (parameterFieldState: FieldState<any>, parameterFieldValue: any = true) =>
  (value: any) => {
    const validationFunction = () => parameterFieldState && parameterFieldState.value === parameterFieldValue;
    return requireIf(validationFunction)(value);
  };

export const customValidationIfFieldState =
  (parameterFieldState: FieldState<any>, parameterFieldValue: any, validation: (value: any) => ValidatorResponse) =>
  (value: any) => {
    const validationFunction = () => parameterFieldState && parameterFieldState.value === parameterFieldValue;
    return validationFunction() && validation(value);
  };

export const customValidationIf =
  (parameter: () => boolean, validation: (value: any) => ValidatorResponse) => (value: any) => {
    return parameter() && validation(value);
  };

export const requireIf = (func: () => boolean) => (value: any) => {
  return func() && Validators.required(value);
};

export const areTheSameItem = (item1: any, item2: any): boolean =>
  (item1.mx_internal_uid && item2.mx_internal_uid && item1.mx_internal_uid === item2.mx_internal_uid) ||
  (item1.id && item2.id && item1.id === item2.id);

export const pasteLatAndLong = (
  latitude: FieldState<any>,
  longitude: FieldState<any>,
  oldValue: any,
  newValue: any,
  intlOld: any,
  pasteLat: boolean
) => {
  if (latitude && longitude) {
    const separator = `${newValue || ''}`.includes(',') ? ',' : ';';
    const valueSplit = `${newValue || ''}`.split(separator);
    if (valueSplit && valueSplit.length === 2) {
      const newLat = Number(`${valueSplit[0] || ''}`.toString().trimLeft().trimRight());
      const newLong = Number(`${valueSplit[1] || ''}`.toString().trimLeft().trimRight());
      if (
        Number.isFinite(newLat) &&
        Number.isFinite(newLong) &&
        newLat >= -90 &&
        newLat <= 90 &&
        newLong >= -180 &&
        newLong <= 180
      ) {
        latitude.onChange(newLat);
        longitude.onChange(newLong);
      } else {
        showToastNotification({
          message: intl('app.errors.latAndLongPastedNotValid'),
          type: 'danger'
        });
      }
      return true;
    }
    if (valueSplit && valueSplit.length === 1) {
      const item = Number(`${valueSplit[0] || ''}`.toString().trimLeft().trimRight());
      if (item) {
        if (pasteLat) {
          if (Number.isFinite(item) && item >= -90 && item <= 90) {
            latitude.onChange(item);
          } else {
            showToastNotification({
              message: intl('app.errors.latitudePastedNotValid'),
              type: 'danger'
            });
            latitude.onChange(oldValue);
          }
        } else {
          if (Number.isFinite(item) && item >= -180 && item <= 180) {
            longitude.onChange(item);
          } else {
            showToastNotification({
              message: intl('app.errors.longitudePastedNotValid'),
              type: 'danger'
            });
            longitude.onChange(oldValue);
          }
        }
      } else {
        showToastNotification({
          message: intl('app.errors.latAndLongPastedNotValid'),
          type: 'danger'
        });
      }
    } else {
      showToastNotification({
        message: intl('app.errors.latAndLongPastedNotValid'),
        type: 'danger'
      });
    }
  }
};

export const cleanObjectFromEmptyKeys = (value: any) => {
  const keys: string[] = Object.keys(value || {});
  keys.forEach((key: string) => {
    if (!value[key] && typeof value[key] !== 'boolean') {
      delete value[key];
    }
  });
  return value;
};

export const generateAuthenticationKey = (user: any) => {
  const plainText = `${user?.username}${user?.alias?.authenticationPrivateKey}`.toUpperCase();
  return md5(`${plainText}${moment().format('YYYY-MM-DD')}${new Date().getUTCHours()}`);
};

export const dateComparatorFromString = (format?: string) => (date1: string, date2: string) => {
  const date1Moment = moment(date1, format);
  const date2Moment = moment(date2, format);
  return date1Moment.diff(date2Moment, 'seconds');
};

export const dateComparatorFromObject = (date1: DateTimeType, date2: DateTimeType) => {
  if (date1?.date && date1?.time && date2?.date && date2?.time) {
    const date1Moment = moment(`${get(date1, 'date')} ${get(date1, 'time')}`, 'MM/DD/YYYY hh:mm A');
    const date2Moment = moment(`${get(date2, 'date')} ${get(date2, 'time')}`, 'MM/DD/YYYY hh:mm A');
    return date1Moment.diff(date2Moment, 'seconds');
  }
};

export const hexToRGBA = (color: string, opacity: number) => {
  const rgb: any[] =
    color
      ?.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m: any, r: any, g: any, b: any) => `# r r g g b b`)
      ?.substring(1)
      ?.match(/.{2}/g)
      ?.map(x => parseInt(x, 16)) ?? [];

  return `rgba(${rgb.join(',')},${opacity})`;
};

/**
 *
 * @param data number in meters
 * @param convertTo miles | km | ft
 */
export const convertMetersTo = (data: number, convertTo: string) => {
  let valueTo;
  switch (convertTo) {
    case 'mi': {
      valueTo = 0.00062137; // to miles
      break;
    }
    case 'in': {
      valueTo = 39.37; // to inches
      break;
    }
    case 'ft': {
      valueTo = 3.2808; // to Ft
      break;
    }
    case 'km': {
      valueTo = 0.001; // to km
      break;
    }
    default: {
      valueTo = 0.00062137; // to miles
      break;
    }
  }
  return parseFloat(Math.round(data * valueTo).toString()).toFixed(2);
};

export const convertMetersToByDistance = (data: number) => {
  if (data < 161) return `${convertMetersTo(data, 'ft')} ft`;
  if (data >= 161) return `${convertMetersTo(data, 'mi')} mi`;
};

const calcHours = (time: number) => {
  const result = (time / 3600).toFixed(4);
  const hours = result.split('.')[0];
  const min = Math.round(+`0.${result.split('.')[1]}` * 60);
  return `${hours}h ${min}min`;
};

const getFixedTime = (data: number) => parseFloat(Math.round(data).toString()).toFixed(2);

export const convertSecondsToByDuration = (data: number) => {
  if (data < 60) return `${getFixedTime(data)} sec`;
  if (data >= 60 && data < 3600) return `${getFixedTime(data / 60)} min`;
  if (data >= 3600) return `${calcHours(data)}`;
};

export const lightenDarkenColor = (argCol: string, amt: number) => {
  let usePound = false;
  let colClone = cloneDeep(argCol);

  if (colClone[0] === '#') {
    colClone = colClone.slice(1);
    usePound = true;
  }

  const num = parseInt(colClone, 16);

  let r = (num >> 16) + amt;

  if (r > 255) r = 255;
  else if (r < 0) r = 0;

  let b = ((num >> 8) & 0x00ff) + amt;

  if (b > 255) b = 255;
  else if (b < 0) b = 0;

  let g = (num & 0x0000ff) + amt;

  if (g > 255) g = 255;
  else if (g < 0) g = 0;

  return (usePound ? '#' : '') + (g | (b << 8) | (r << 16)).toString(16);
};

export const getDriverPhone = (driver: Driver) => {
  const phone = get(driver, `phoneBookList[0].phone1`) || get(driver, `phoneBookList[0].phone2`) || '';
  return phone ? setPhoneNumberMask(phone) : '';
};

export const getDriverContractorName = (driver: Partial<Driver>) => {
  return get(driver, `contractor.contact.fullName`, '');
};

export const getParsedDate = (dateObj: any) => {
  return dateObj && dateObj?.date && dateObj?.time ? `${dateObj?.date} ${dateObj?.time}` : '';
};

export const toTitleCase = (value: string, upper?: boolean) => {
  return `${value}`.replace(/\w\S*/g, upper ? upperCase : capitalize);
};

export const sortDays = (hours: any[]): any[] => {
  const list = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  return hours.sort((a, b) => {
    return list.indexOf(a.props.hour.day) > list.indexOf(b.props.hour.day) ? 1 : -1;
  });
};

export const isFirefox = () => {
  return navigator.userAgent.indexOf('Firefox') > -1;
};

export const updateListCounter = (store: SetupStore) => {
  const excludeAction = ['EDIT', 'NONE'];
  if (!excludeAction.includes(store.formState)) {
    store.setCount(store.count + 1);
  }
  store.setFormState('NONE');
};

export const buildShortcuts = (config?: any[]) => {
  const yesterday = moment().subtract(1, 'days').toDate();
  const toDay = moment().toDate();
  const tomorrow = moment().add(1, 'days').toDate();

  const defaultConfiguration = [
    EShortcuts.YESTERDAY,
    EShortcuts.TODAY,
    EShortcuts.TOMORROW,
    EShortcuts.THIS_WEEK,
    EShortcuts.LAST_WEEK,
    EShortcuts.THIS_MONTH,
    EShortcuts.LAST_MONTH,
    EShortcuts.THIS_YEAR
  ];

  const shortcuts: any = [
    {
      type: EShortcuts.YESTERDAY,
      label: intl('app.shortcutsDateRangeFilter.yesterday'),
      dateRange: [ProfileUtils.includeTimeToDate(yesterday, 0, 0), ProfileUtils.includeTimeToDate(yesterday, 23, 59)],
      includeTime: true
    },
    {
      type: EShortcuts.YESTERDAY_7_AM_TO_7_AM,
      label: intl('app.shortcutsDateRangeFilter.yesterday7AmTo7Am'),
      dateRange: [ProfileUtils.includeTimeToDate(yesterday, 7, 0), ProfileUtils.includeTimeToDate(toDay, 6, 59)],
      includeTime: true
    },
    {
      type: EShortcuts.TODAY,
      label: intl('app.shortcutsDateRangeFilter.today'),
      dateRange: [ProfileUtils.includeTimeToDate(toDay, 0, 0), ProfileUtils.includeTimeToDate(toDay, 23, 59)],
      includeTime: true
    },
    {
      type: EShortcuts.TODAY_7_AM_TO_7_AM,
      label: intl('app.shortcutsDateRangeFilter.today7AmTo7Am'),
      dateRange: [ProfileUtils.includeTimeToDate(toDay, 7, 0), ProfileUtils.includeTimeToDate(tomorrow, 6, 59)],
      includeTime: true
    },
    {
      type: EShortcuts.TOMORROW,
      label: intl('app.shortcutsDateRangeFilter.tomorrow'),
      dateRange: [ProfileUtils.includeTimeToDate(tomorrow, 0, 0), ProfileUtils.includeTimeToDate(tomorrow, 23, 59)],
      includeTime: true
    },
    {
      type: EShortcuts.THIS_WEEK,
      label: intl('app.shortcutsDateRangeFilter.thisWeek'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().startOf('week').weekday(0).toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(moment().endOf('week').weekday(6).toDate(), 23, 59)
      ],
      includeTime: true
    },
    {
      type: EShortcuts.LAST_WEEK,
      label: intl('app.shortcutsDateRangeFilter.lastWeek'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().subtract(1, 'weeks').startOf('week').weekday(0).toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(moment().subtract(1, 'weeks').endOf('week').weekday(6).toDate(), 23, 59)
      ],
      includeTime: true
    },
    {
      type: EShortcuts.WEEK_TO_DATE,
      label: intl('app.shortcutsDateRangeFilter.weekToDate'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().startOf('week').weekday(0).toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(toDay, 23, 59)
      ],
      includeTime: true
    },
    {
      type: EShortcuts.THIS_MONTH,
      label: intl('app.shortcutsDateRangeFilter.thisMonth'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().startOf('month').toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(moment().endOf('month').toDate(), 23, 59)
      ],
      includeTime: true
    },
    {
      type: EShortcuts.LAST_MONTH,
      label: intl('app.shortcutsDateRangeFilter.lastMonth'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().subtract(1, 'month').startOf('month').toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(moment().subtract(1, 'month').endOf('month').toDate(), 23, 59)
      ],
      includeTime: true
    },
    {
      type: EShortcuts.MONTH_TO_DATE,
      label: intl('app.shortcutsDateRangeFilter.monthToDate'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().startOf('month').toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(toDay, 23, 59)
      ],
      includeTime: true
    },
    {
      type: EShortcuts.THIS_YEAR,
      label: intl('app.shortcutsDateRangeFilter.thisYear'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().startOf('year').toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(moment().endOf('year').toDate(), 23, 59)
      ]
    },
    {
      type: EShortcuts.YEAR_TO_DATE,
      label: intl('app.shortcutsDateRangeFilter.yearToDate'),
      dateRange: [
        ProfileUtils.includeTimeToDate(moment().startOf('year').toDate(), 0, 0),
        ProfileUtils.includeTimeToDate(toDay, 23, 59)
      ],
      includeTime: true
    },
    {
      type: EShortcuts.USER_DEFINED,
      label: intl('app.shortcutsDateRangeFilter.userDefined'),
      dateRange: [null, null]
    }
  ];

  const generateShortcuts = (shortcutsEnables: EShortcuts[]) => {
    return shortcuts.filter((item: IShortcuts) => shortcutsEnables.includes(item.type));
  };

  return generateShortcuts(!!config?.length ? config : defaultConfiguration);
};

export const splitTextBySeparation = (text: string) => {
  return text.split('\n');
};

export const applyValueToFilters = (previous: IFilterType[], queryFilters: IFilterType[]): IFilterType[] => {
  return map(queryFilters, (item: IFilterType) => {
    const findInPrevious = find(previous, { field: item.field });
    if (findInPrevious) {
      let response = { ...item, value: findInPrevious.value };
      if (findInPrevious?.dateType && item?.dateType) response = { ...response, dateType: findInPrevious?.dateType };
      if (!!findInPrevious?.filters?.length && item?.filters?.length) {
        response = { ...response, filters: applyValueToFilters(findInPrevious.filters, item.filters) };
      }
      return response;
    }
    return item;
  });
};

export const isSourceDependencyForm = (name: string): boolean => {
  return name.toLowerCase().includes('dynamic_form');
};

export const mapConditionalsToReadyFormat = (conditionals: IConditionalsType[]) => {
  return conditionals.map((value: IConditionalsType) => {
    if (!value.hasOwnProperty('format')) {
      return { ...value, format: '' };
    }
    return value;
  });
};

export const duplicateSourceTypeInAuditTrail = (amount: number, sourceType: string) => {
  return Array(amount).fill(sourceType);
};

export const getContactBasedOnString = (fullName: string = '') => {
  const splittedFullName = fullName.split(',');
  return {
    firstName: `${splittedFullName?.[1]}`.trim(),
    lastName: `${splittedFullName?.[0]}`.trim()
  };
};

export const orderProfiles = (profiles: IVeltraxProfileType[]): IVeltraxProfileType[] => {
  if (profiles?.length) {
    const orders: Record<string, IVeltraxProfileType[]> = groupBy(profiles, 'isActive');
    const newActive: IVeltraxProfileType[] = orders?.true ? orderBy(orders['true'], 'name') : [];
    const newInactive: IVeltraxProfileType[] = orders?.false ? orderBy(orders['false'], 'name') : [];
    return [...newActive, ...newInactive];
  }
  return [];
};

export const filterHasNoValueFn = (filter: IFilterType, allowNull?: boolean) => {
  const { value } = filter;
  return !!allowNull ? value === '' || value?.length === 0 : value === '' || value === null || value?.length === 0;
};

export const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

export const isDesktop = () => getDeviceType() === 'desktop';

export const putZeroToSingleNumber = (value: number) =>
  value.toLocaleString('en-US', {
    minimumIntegerDigits: 2,
    useGrouping: false
  });

export const loadsInfo = (argLoads: any) => {
  const loads: Partial<Load>[] = [];
  argLoads.forEach((el: any) => {
    const newLoad = cloneDeep(el);
    if (newLoad) {
      loads.push(newLoad);
    }
  });
  const parsedLoadslist: any[] = [];
  loads.forEach((load: any) => {
    const info = {
      confirmationNumber: get(load, 'confirmationNos.[0]') || '',
      bolNumber: load?.billOfLadingNumber || '',
      pickUpName: get(load, 'pickUpList.[0].contact.fullName') || '',
      dropOffName: get(load, 'dropOffList.[0].contact.fullName') || ''
    };
    parsedLoadslist.push(info);
  });
  return parsedLoadslist;
};
